import React, { useState, useMemo } from "react";
import { cn } from "../utils/cn";
import type {
  WhatIfSliderValues,
  WhatIfSliderKey,
} from "../hooks/useWhatIfScenario";
import { getSliderConfigs } from "./whatIfSliderConfigs";

interface WhatIfPanelProps {
  sliderValues: WhatIfSliderValues | null;
  /** Original (saved) values for comparison display */
  originalValues?: WhatIfSliderValues;
  isActive: boolean;
  /** User's current age — used to set dynamic slider minimums */
  currentAge: number;
  onSliderChange: (key: WhatIfSliderKey, value: number) => void;
  onReset: () => void;
  onSave: () => void;
}

/** Compute the fill percentage for a slider track */
function fillPercent(value: number, min: number, max: number): number {
  return ((value - min) / (max - min)) * 100;
}

/** Round to nearest dollar for comparison — ignore sub-dollar differences */
function sameDollarValue(a: number, b: number): boolean {
  return Math.round(a) === Math.round(b);
}

export const WhatIfPanel: React.FC<WhatIfPanelProps> = React.memo(
  ({
    sliderValues,
    originalValues,
    isActive,
    currentAge,
    onSliderChange,
    onReset,
    onSave,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const sliderConfigs = useMemo(
      () => getSliderConfigs(currentAge),
      [currentAge]
    );

    return (
      <div
        className={cn("what-if-panel", isActive && "what-if-panel--active")}
        id="what-if-panel"
      >
        {/* Toggle button */}
        <button
          type="button"
          className="what-if-toggle"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          <span className="what-if-toggle-icon">🔮</span>
          <span className="what-if-toggle-text">What if...?</span>
          <span className="what-if-toggle-arrow">{isExpanded ? "▼" : "→"}</span>
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="what-if-content">
            {/* Panel header with close button */}
            <div className="what-if-panel-header">
              <span className="what-if-panel-header-title">
                Adjust Assumptions
              </span>
              <button
                type="button"
                className="what-if-close-btn"
                onClick={() => setIsExpanded(false)}
                aria-label="Close what-if panel"
                title="Close panel"
              >
                ✕
              </button>
            </div>

            {/* Sliders */}
            <div className="what-if-sliders">
              {sliderValues &&
                sliderConfigs.map((config) => {
                  const dataValue = sliderValues[config.key];
                  const sliderValue = config.toSlider(dataValue);
                  const pct = fillPercent(sliderValue, config.min, config.max);

                  // Round to $1 precision to avoid spurious "(was X)" labels
                  const origDataValue = originalValues?.[config.key];
                  const showOriginal =
                    origDataValue !== undefined &&
                    !sameDollarValue(origDataValue, dataValue);

                  return (
                    <div key={config.key} className="what-if-slider-row">
                      <div className="what-if-slider-header">
                        <label
                          htmlFor={`what-if-${config.key}`}
                          className="what-if-slider-label"
                        >
                          {config.label}
                        </label>
                        <span className="what-if-slider-value">
                          {config.format(dataValue)}
                          {showOriginal && (
                            <span className="what-if-slider-original">
                              {" "}
                              (was {config.format(origDataValue)})
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        id={`what-if-${config.key}`}
                        type="range"
                        className="what-if-slider-input"
                        min={config.min}
                        max={config.max}
                        step={config.step}
                        value={sliderValue}
                        style={
                          { "--fill-percent": `${pct}%` } as React.CSSProperties
                        }
                        onChange={(e) =>
                          onSliderChange(
                            config.key,
                            config.fromSlider(parseFloat(e.target.value))
                          )
                        }
                      />
                    </div>
                  );
                })}
            </div>

            {/* Action buttons — always visible, disabled until active */}
            <div className="what-if-actions">
              <button
                type="button"
                className="what-if-btn-reset"
                onClick={onReset}
                disabled={!isActive}
                title={!isActive ? "Adjust a slider to enable" : undefined}
              >
                Reset
              </button>
              <button
                type="button"
                className="what-if-btn-save"
                onClick={onSave}
                disabled={!isActive}
                title={!isActive ? "Adjust a slider to enable" : undefined}
              >
                Save these settings
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

WhatIfPanel.displayName = "WhatIfPanel";
